import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Trophy, LogOut, Zap, FileText, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";

const GRADE_VALUES = { 'A': 4, 'B': 3, 'C': 2, 'D': 1 };
const GRADE_COLORS = {
  'A': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'B': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'C': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'D': { bg: 'bg-orange-100', text: 'text-orange-700' }
};

const GradeBadge = ({ grade }) => {
  const colors = GRADE_COLORS[grade] || GRADE_COLORS['C'];
  return (
    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl font-bold text-lg ${colors.bg} ${colors.text}`}>
      {grade}
    </span>
  );
};

const TrendIndicator = ({ current, previous }) => {
  if (!previous) return null;
  const currentVal = GRADE_VALUES[current] || 0;
  const prevVal = GRADE_VALUES[previous] || 0;
  const diff = currentVal - prevVal;

  if (diff > 0) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (diff < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [quarterlyReports, setQuarterlyReports] = useState({});
  const [loading, setLoading] = useState(true);
  const userName = localStorage.getItem('userName');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const studentsRes = await api.get("/students");
      setStudents(studentsRes.data);

      const quarterlyPromises = studentsRes.data.map(student =>
        api.get(`/reports/quarterly/${student.id}`).catch(() => ({ data: [] }))
      );

      const quarterlyResults = await Promise.all(quarterlyPromises);

      const quarterlyMap = {};
      studentsRes.data.forEach((student, idx) => {
        quarterlyMap[student.id] = quarterlyResults[idx].data;
      });
      setQuarterlyReports(quarterlyMap);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Logged out successfully");
    navigate('/login');
  };

  const getOverallGrade = (report) => {
    if (!report) return null;
    const skills = report.grades.performance;
    const avg = (GRADE_VALUES[skills.passing] + GRADE_VALUES[skills.shooting] +
      GRADE_VALUES[skills.dribbling] + GRADE_VALUES[skills.heading] +
      GRADE_VALUES[skills.ball_control] + GRADE_VALUES[skills.match_play]) / 6;
    if (avg >= 3.5) return 'A';
    if (avg >= 2.5) return 'B';
    if (avg >= 1.5) return 'C';
    return 'D';
  };

  const getInsight = (reports) => {
    if (reports.length < 2) return null;
    const latest = reports[0];
    const previous = reports[1];

    let improved = 0, declined = 0;
    const skills = Object.keys(latest.grades.performance);
    skills.forEach(skill => {
      const diff = GRADE_VALUES[latest.grades.performance[skill]] - GRADE_VALUES[previous.grades.performance[skill]];
      if (diff > 0) improved++;
      if (diff < 0) declined++;
    });

    if (improved > declined) return { type: 'up', text: `Improved in ${improved} skills` };
    if (declined > improved) return { type: 'down', text: `Needs focus in ${declined} areas` };
    return { type: 'same', text: 'Consistent performance' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Premium Header */}
      <nav className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/gfa-logo.png?v=5" alt="GFA Logo" className="w-10 h-10 object-contain rounded-full bg-white p-0.5" />
              <div>
                <h1 className="text-xl font-bold" data-testid="parent-dashboard-title">German Football Academy</h1>
                <p className="text-sm text-slate-400">Parent Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-emerald-500/20 px-4 py-2 rounded-full border border-white/10">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-white/90">STADIA</span>
              </div>
              <span className="text-sm font-medium hidden md:block" data-testid="parent-name">{userName}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="parent-logout-btn"
                className="hover:bg-white/10 text-white rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome back! 👋</h2>
          <p className="text-slate-600">Track your child's football progress with quarterly performance reports</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">Loading...</div>
        ) : students.length === 0 ? (
          <Card className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm" data-testid="no-children-message">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl flex items-center justify-center mb-4">
                <Trophy className="w-10 h-10 text-slate-400" />
              </div>
              <p className="text-lg text-slate-600">No players found. Contact the academy to link your child's profile.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {students.map((student) => {
              const reports = quarterlyReports[student.id] || [];
              const latestReport = reports[0];
              const previousReport = reports[1];
              const overallGrade = getOverallGrade(latestReport);
              const insight = getInsight(reports);

              return (
                <Card
                  key={student.id}
                  className="shadow-xl border-0 rounded-3xl bg-white/80 backdrop-blur-sm overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/student/${student.id}`)}
                  data-testid={`parent-student-card-${student.id}`}
                >
                  {/* Card Header with Gradient */}
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                          <Trophy className="w-7 h-7" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold" data-testid={`parent-student-name-${student.id}`}>
                            {student.name}
                          </h3>
                          <p className="text-emerald-100 text-sm">Age: {student.age} • {student.skill_level}</p>
                        </div>
                      </div>
                      {overallGrade && (
                        <div className="text-center">
                          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                            <span className={`text-3xl font-bold ${GRADE_COLORS[overallGrade].text}`}>{overallGrade}</span>
                          </div>
                          <p className="text-xs text-emerald-100 mt-1">Overall</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {latestReport ? (
                      <div className="space-y-5">
                        {/* Latest Quarter Info */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-emerald-600" />
                            <span className="font-semibold text-slate-800">{latestReport.quarter} {latestReport.year}</span>
                          </div>
                          <span className="text-sm text-slate-500">{reports.length} report{reports.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* Skills Preview */}
                        <div className="grid grid-cols-6 gap-2">
                          {Object.entries(latestReport.grades.performance).map(([skill, grade]) => (
                            <div key={skill} className="text-center">
                              <GradeBadge grade={grade} />
                              <p className="text-xs text-slate-500 mt-1 truncate">{skill.replace('_', ' ').slice(0, 4)}</p>
                            </div>
                          ))}
                        </div>

                        {/* Insight */}
                        {insight && (
                          <div className={`flex items-center gap-2 p-3 rounded-xl ${insight.type === 'up' ? 'bg-emerald-50 text-emerald-700' :
                            insight.type === 'down' ? 'bg-amber-50 text-amber-700' :
                              'bg-slate-50 text-slate-700'
                            }`}>
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm font-medium">{insight.text}</span>
                          </div>
                        )}

                        {/* General Grades */}
                        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Regularity:</span>
                            <GradeBadge grade={latestReport.grades.regularity} />
                            {previousReport && <TrendIndicator current={latestReport.grades.regularity} previous={previousReport.grades.regularity} />}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Discipline:</span>
                            <GradeBadge grade={latestReport.grades.discipline} />
                            {previousReport && <TrendIndicator current={latestReport.grades.discipline} previous={previousReport.grades.discipline} />}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                          <FileText className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600">No quarterly reports yet</p>
                        <p className="text-sm text-slate-500">Reports will appear here once submitted by the coach</p>
                      </div>
                    )}

                    <Button
                      className="w-full h-12 mt-4 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-900 hover:to-black text-white font-semibold rounded-xl shadow-lg group-hover:shadow-xl transition-all"
                      data-testid={`parent-view-student-btn-${student.id}`}
                    >
                      View Full Performance Report
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
